import { useEffect, useState } from 'react';

/**
 * Section nào đang ở "vùng đọc" — dùng để tô mục nav đang active.
 *
 * Thiết kế làm việc này bằng listener `scroll` + `getBoundingClientRect()` cho TỪNG section
 * trong MỖI event (`paintNav()` trong khối `<script type="text/x-dc">`, template.html dòng
 * 1310–1352). Đó là đo layout mỗi frame cuộn: jank thấy được, và INP xấu.
 *
 * Ở đây dùng IntersectionObserver, không có scroll handler nào.
 */

/**
 * Điều kiện của thiết kế là `r.top <= 140 && r.bottom > 140` — tức "section chứa ĐIỂM y = 140px",
 * 140px là chiều cao header sticky cộng lề đọc.
 *
 * IntersectionObserver không nhận một điểm, chỉ nhận một hình chữ nhật, nên `rootMargin` cắt
 * 140px trên và phần dưới để còn lại một DẢI mỏng ngay dưới header. Khác biệt duy nhất so với
 * thiết kế: hai section có thể cùng nằm trong dải. Cách chọn khi đó nói ở trong callback.
 */
const BAND_TOP = 140;

/** Tỉ lệ khung nhìn mà dải đọc chiếm ở màn cao — 40%, đúng bản đầu. */
const BAND_RATIO = 0.4;

/**
 * Sàn của dải đọc, tính bằng px.
 *
 * VÌ SAO CẦN SÀN: công thức tỉ lệ trần cho dải `= 0.4H − 140`, **âm khi `H < 350`**. Dải âm nghĩa
 * là observer không bao giờ báo giao ⇒ KHÔNG mục nav nào sáng, im lặng và vĩnh viễn. Điện thoại
 * xoay ngang chạm ngưỡng đó thật (iPhone SE landscape còn ~315px sau thanh trình duyệt). Đây là
 * số học thuần chứ không phải chỗ mù của jsdom, nên nó đóng được bằng test.
 */
const BAND_MIN = 80;

/**
 * `rootMargin` tính theo chiều cao khung nhìn THẬT, không phải chuỗi hằng.
 *
 * Cận dưới trả về bằng px (không phải `%`) vì sàn chỉ diễn đạt được bằng px.
 */
function readingBandMargin(viewportHeight: number): string {
  const band = Math.max(BAND_MIN, viewportHeight * BAND_RATIO - BAND_TOP);
  // Kẹp ≥ 0: ở khung nhìn thấp bất thường (< 220px) thì thà để dải chạm đáy còn hơn cắt âm.
  const bottom = Math.max(0, viewportHeight - BAND_TOP - band);
  return `-${String(BAND_TOP)}px 0px -${String(Math.round(bottom))}px 0px`;
}

/**
 * Ký tự nối để gộp `ids` thành một khoá nguyên thuỷ cho deps của effect.
 *
 * Vì sao không đưa thẳng mảng vào deps: phía gọi viết `useScrollSpy(['modules', 'collab'])` là
 * chuyện bình thường, và mảng literal là tham chiếu MỚI ở mỗi render ⇒ effect dựng lại observer
 * mỗi render. Vì sao không dùng deps rỗng: khi đó `ids` bị đóng băng ở lần render đầu và mọi
 * thay đổi về sau bị nuốt im lặng. Khoá theo NỘI DUNG giải quyết cả hai.
 *
 * NUL chứ không phải dấu phẩy: nó không hợp lệ trong id của HTML nên không id nào tự tách được
 * thành hai.
 */
const SEP = '\u0000';

export function useScrollSpy(ids: readonly string[], aliases: Record<string, string> = {}): string {
  const key = ids.join(SEP);
  const [visibleId, setVisibleId] = useState('');
  /**
   * Theo dõi chiều cao khung nhìn vì `rootMargin` phụ thuộc nó (xem `readingBandMargin`). Đây là
   * listener `resize`, KHÔNG phải `scroll` — nó bắn vài lần khi xoay máy, không phải mỗi frame,
   * nên không mâu thuẫn với lý do hook này tồn tại.
   *
   * `setState` với cùng giá trị là no-op của React, nên kéo giãn cửa sổ trên desktop không dựng
   * lại observer ở mỗi pixel.
   */
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === 'undefined' ? 0 : window.innerHeight,
  );

  useEffect(() => {
    const sync = () => {
      setViewportHeight(window.innerHeight);
    };
    sync();
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('resize', sync);
    };
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const order = key === '' ? [] : key.split(SEP);
    const visible = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const item of entries) {
          if (item.isIntersecting) visible.add(item.target.id);
          else visible.delete(item.target.id);
        }
        // Lấy theo THỨ TỰ KHAI trong `ids`, không theo thứ tự callback: khi hai section cùng nằm
        // trong dải đọc thì phần tử đầu là phần tử trên cùng theo thứ tự tài liệu, tức chính
        // section chứa y = 140 mà thiết kế chọn. Theo thứ tự callback thì mục nav nhảy theo hướng
        // cuộn.
        setVisibleId(order.find((id) => visible.has(id)) ?? '');
      },
      { rootMargin: readingBandMargin(viewportHeight), threshold: 0 },
    );

    for (const id of order) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }

    return () => {
      observer.disconnect();
    };
  }, [key, viewportHeight]);

  // Alias áp lúc RENDER chứ không lúc callback: `aliases` nhờ vậy không phải là phụ thuộc của
  // effect (không dựng lại observer khi phía gọi truyền object literal), và đổi bảng gộp có hiệu
  // lực ngay thay vì đợi lượt cuộn kế tiếp.
  //
  // Thiết kế gộp `pm → collab` và `knowledge → ai` vì nav chỉ có 6 mục cho 8 section theo dõi;
  // bảng gộp do phía gọi truyền vào để hook không biết gì về nội dung trang.
  if (visibleId === '') return '';
  return aliases[visibleId] ?? visibleId;
}
